import { useCallback, useState } from 'react'
import { ButtonPrimary } from '../../../../../../../../../../../baseComponents/Button'
import { ModalOpenedSetTy, ModalOpenedTy } from '../../../../../../../../../../../baseComponents/Modal/types'
import TwinTrans from '../../../../../../../../../../../baseComponents/TwinTrans'
import FormRenderer from '../../../../../../../../../../../forms/FormRenderer'
import { FormRender } from '../../../../../../../../../../../forms/FormRenderer/types'
import { InputWithLabelMargin } from '../../../../../../../../../../../forms/Input'
import { getDictionaryStates } from '../../../../../../../../../../../utils/globals/countries'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { getDictionaryCountries } from '../../../../../../../../../../../utils/reducers/getters'
import { changeErrorMessage } from '../../../../../../../../../../../utils/reducers/reduxDispatch'
import { ShippingZoneGroupModelType } from '@teinor/erp/types/company/config/shipping/shippingZoneGroup'
import { ShippingZoneModelType } from '@teinor/erp/types/company/config/shipping/shippingZoneGroup/shippingZone'
import { sortCountries } from '../../../../../../../../../../../utils/globals/sortCountries'

interface ModalCEShippingFormProps extends ModalCEShippingFormLogicProps {
    data: ShippingZoneGroupModelType
}

const ModalCEShippingForm: React.FC<ModalCEShippingFormProps> = ({ setData, data }) => {
    const { openCreateShippingZonesCreate, setOpenCreateShippingZonesCreate, handleOnClickAddShippingZone } = useModalCEShippingFormLogic({setData})
    return (
        <div className='mb-30'>
            <ModalCEShippingZonesServiceHeader {...{ openCreateShippingZonesCreate, setOpenCreateShippingZonesCreate, setData, data }} />
            {openCreateShippingZonesCreate ? <ShippingZonesServiceInputs {...{ data, handleOnClickAddShippingZone }} /> : null}
        </div>
    )
}

interface ModalCEShippingFormLogicProps {
    setData: React.Dispatch<React.SetStateAction<ShippingZoneGroupModelType>>
}

const useModalCEShippingFormLogic = ({ setData }: ModalCEShippingFormLogicProps) => {
    const { t } = useTwinTranslation()
    const [openCreateShippingZonesCreate, setOpenCreateShippingZonesCreate] = useState<ModalOpenedTy>(null)
    const countryIsRequiredError = t('countryIsRequired', 'El país es obligatorio')
    const handleOnClickAddShippingZone = useCallback((shippingZoneInnerInputs: ShippingZoneModelType | null) => {
        if (shippingZoneInnerInputs && !shippingZoneInnerInputs.country) {
            changeErrorMessage(countryIsRequiredError)
        } else {
            setData((old) => {
                if (!old) {
                    return null
                }
                const copy = JSON.parse(JSON.stringify(old))
                copy.ShippingZones.push(shippingZoneInnerInputs)
                return { ...copy, ShippingZones: [...copy.ShippingZones] }
            })
            setOpenCreateShippingZonesCreate(null)
        }
    }, [setData, countryIsRequiredError])

    return { openCreateShippingZonesCreate, setOpenCreateShippingZonesCreate, handleOnClickAddShippingZone }
}



interface ModalCEShippingZonesServiceHeaderProps extends ModalCEShippingZonesServiceHeaderLogicProps {
    setOpenCreateShippingZonesCreate: ModalOpenedSetTy
    openCreateShippingZonesCreate: ModalOpenedTy
    data: ShippingZoneGroupModelType
}

const ModalCEShippingZonesServiceHeader: React.FC<ModalCEShippingZonesServiceHeaderProps> = ({ setOpenCreateShippingZonesCreate, openCreateShippingZonesCreate, data, setData }) => {
    const { t } = useTwinTranslation()
    const { handleOnChangeName } = useModalCEShippingZonesServiceHeaderLogic({ setData })
    const title = data?.id ? t('updateZoneShippingService', 'Actualizar zona de envío') : t('addShippingZone', 'Añadir zona de envío')
    return (
        <div className='flex flex-col'>
            <div className='flex items-center'>
                <h2>{title}</h2>
            </div>
            <InputWithLabelMargin label={t('name', 'Nombre')} value={data?.name} onChange={handleOnChangeName} name='name' required className='mt-10' />
            <span className='ml-auto text-16 text-green-21 cursor-pointer mt-20' onClick={() => setOpenCreateShippingZonesCreate((old) => old ? null : true)}>
                {openCreateShippingZonesCreate ? <TwinTrans transKey='close'>Cerrar</TwinTrans> : <TwinTrans transKey='addZoneShippingService'>Añadir zona de envío</TwinTrans>}
            </span>
        </div>
    )
}

interface ModalCEShippingZonesServiceHeaderLogicProps {
    setData: React.Dispatch<React.SetStateAction<ShippingZoneGroupModelType>>
}

const useModalCEShippingZonesServiceHeaderLogic = ({ setData }: ModalCEShippingZonesServiceHeaderLogicProps) => {
    const handleOnChangeName = useCallback((value: string) => {
        setData((old) => {
            if (!old) {
                return null
            }
            const copy = JSON.parse(JSON.stringify(old))
            return { ...copy, name: value }
        })
    }, [setData])
    return { handleOnChangeName }
}


interface ShippingZonesServiceInputsProps {
    handleOnClickAddShippingZone: (shippingZoneInnerInputs: ShippingZoneModelType) => void
}

const ShippingZonesServiceInputs: React.FC<ShippingZonesServiceInputsProps> = ({handleOnClickAddShippingZone }) => {
    const { t } = useTwinTranslation()
    const { handleAddShippingZoneCountryStateAndName, shippingZoneInnerInputs } = useShippingZonesServiceInputsLogic()
    const fields: FormRender<any> = [
        {
            title: t('shippingZone', 'Zona de envío'),
            cols: 2,
            elements: [
                {
                    name: 'country',
                    label: t('country', 'País'),
                    component: 'CustomSelectWithSearchBar',
                    items: getDictionaryCountries(),
                    value: shippingZoneInnerInputs?.country,
                    onChange: (value) => handleAddShippingZoneCountryStateAndName(value, 'country'),
                    required: true,
                    sortFunction: sortCountries
                },
                {
                    name: 'state',
                    label: t('state', 'Provincia'),
                    component: 'CustomSelectWithSearchBar',
                    items: getDictionaryStates(shippingZoneInnerInputs?.country || ''),
                    value: shippingZoneInnerInputs?.state,
                    onChange: (value) => handleAddShippingZoneCountryStateAndName(value, 'state'),
                },
                {
                    name: 'postal_code',
                    label: t('postalCode', 'Código Postal'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    value: shippingZoneInnerInputs?.postal_code || '',
                    onChange: (value) => handleAddShippingZoneCountryStateAndName(value, 'postal_code')
                },
            ]
        }
    ]
    return (
        <div className='flex flex-col'>
            <FormRenderer sections={fields} className='mt-10' />
            <ButtonPrimary type='button' className='ml-auto' onClick={() => {handleOnClickAddShippingZone(shippingZoneInnerInputs)}}><TwinTrans transKey={'add'}>Añadir</TwinTrans></ButtonPrimary>
        </div>
    )
}

const useShippingZonesServiceInputsLogic = () => {
    
    const [shippingZoneInnerInputs, setShippingZoneInnerInputs] = useState<ShippingZoneModelType>({ country: '', postal_code: '', state: '', ShippingZoneGroupId: 0 })
    const handleAddShippingZoneCountryStateAndName = useCallback((value: any, field: string) => {
        setShippingZoneInnerInputs((old) => {
            if (!old) {
                return null
            }
            const copy = JSON.parse(JSON.stringify(old))
            if (field === 'country') {
                copy.country = value
            } else if (field === 'state') {
                copy.state = value
            } else {
                copy.postal_code = value
            }
            return {...copy}
        })
    }, [setShippingZoneInnerInputs])
    return { handleAddShippingZoneCountryStateAndName, shippingZoneInnerInputs }
}


export default ModalCEShippingForm